import type { PageSetup } from '../model';
import type { AtomProperties, BondProperties, GraphEdge, GraphNode, Point2D } from '../model';
import type { PaperSize } from './geometry.ts';

// #587 M2: hand-authored TypeScript mirror of drawing-v11.schema.json's `markushDrawing` family
// (ADR 0013). The v11 schema has never had generated types (only v10's DrawingDocument does), so
// these track the schema defs by hand -- keep them in step with packages/contracts.

/** The schema's `markushCoordinates`: an embedded coordinate set with no id/kind/isomer fields. */
export interface MarkushCoordinates {
  readonly width: number;
  readonly height: number;
  readonly coordinates: Readonly<Record<string, Point2D>>;
}

/** v11's `graph` def: topology only, no id/revision (unlike v10's Graph). */
export interface MarkushGraph {
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
}

/** The fixed core. A graph node with no `atoms` entry is a variable-position placeholder. No
 *  stereochemistry in v1 (the schema omits it deliberately). */
export interface MarkushScaffold {
  readonly graph: MarkushGraph;
  readonly atoms: Readonly<Record<string, AtomProperties>>;
  readonly bonds: Readonly<Record<string, BondProperties>>;
  readonly formalCharges: Readonly<Record<string, number>>;
  readonly coordinateSet: MarkushCoordinates;
}

/** One concrete member of an enumerated R-group: always fully concrete (fragments never hold a
 *  variable position), with `openValenceAtomId` naming the atom that grafts onto the scaffold.
 *  Its coordinates are the fragment's own local space -- registration onto the scaffold is an
 *  open question for M3c/M4 (docs/markush-structures-model.md). */
export interface MarkushFragment extends MarkushScaffold {
  readonly openValenceAtomId: string;
}

export const HOMOLOGY_CLASSES = [
  'alkyl',
  'haloalkyl',
  'aryl',
  'heteroaryl',
  'cycloalkyl',
  'alkenyl',
  'alkynyl'
] as const;

export type HomologyClass = (typeof HOMOLOGY_CLASSES)[number];

export type RGroupDefinition =
  | { readonly kind: 'enumerated'; readonly members: readonly MarkushFragment[] }
  | { readonly kind: 'homology'; readonly class: HomologyClass };

export interface RGroupPosition {
  readonly kind: 'r-group';
  readonly atomId: string;
  readonly rGroupLabel: string;
}

/** A bond drawn into a ring's interior. `atomId` is a bond-less placeholder node; `ringAtomIds`
 *  must form exactly one simple ring (single-ring only in v1 -- decided September 24, 2026). */
export interface AttachmentPointPosition {
  readonly kind: 'attachment-point';
  readonly atomId: string;
  readonly ringAtomIds: readonly string[];
  readonly rGroupLabel: string;
}

export interface AtomListPosition {
  readonly kind: 'atom-list';
  readonly atomId: string;
  readonly allowedAtomicNumbers: readonly number[];
}

/** Spans real, concrete scaffold atoms/bonds -- only the repetition count varies. An omitted
 *  `countMax` means open-ended (unbounded). */
export interface RepeatUnitPosition {
  readonly kind: 'repeat-unit';
  readonly fragmentAtomIds: readonly string[];
  readonly fragmentBondIds: readonly string[];
  readonly countMin: number;
  readonly countMax?: number;
}

export type PlaceholderPosition = RGroupPosition | AttachmentPointPosition | AtomListPosition;
export type VariablePosition = PlaceholderPosition | RepeatUnitPosition;

export interface MarkushDrawingDocument {
  readonly schemaVersion: 'chem-drawing.v11';
  readonly kind: 'markush';
  readonly scaffold: MarkushScaffold;
  readonly rGroups: Readonly<Record<string, RGroupDefinition>>;
  readonly variablePositions: readonly VariablePosition[];
  // #772 -- the drawing's page (absent before #772: the default Letter applies).
  readonly page?: PageSetup;
}

export function createEmptyMarkushDrawing(size: PaperSize): MarkushDrawingDocument {
  return {
    schemaVersion: 'chem-drawing.v11',
    kind: 'markush',
    scaffold: {
      graph: { nodes: [], edges: [] },
      atoms: {},
      bonds: {},
      formalCharges: {},
      coordinateSet: { width: Math.max(1, size.width), height: Math.max(1, size.height), coordinates: {} }
    },
    rGroups: {},
    variablePositions: []
  };
}

export function isPlaceholderPosition(position: VariablePosition): position is PlaceholderPosition {
  return position.kind !== 'repeat-unit';
}

/** The variable position occupying a scaffold placeholder node, if any. */
export function placeholderPositionAt(
  document: MarkushDrawingDocument,
  atomId: string
): PlaceholderPosition | undefined {
  return document.variablePositions.find(
    (position): position is PlaceholderPosition => isPlaceholderPosition(position) && position.atomId === atomId
  );
}

/** Every R-group label referenced from a variable position, in first-use order. */
export function referencedRGroupLabels(document: MarkushDrawingDocument): readonly string[] {
  const labels = document.variablePositions.flatMap((position) =>
    position.kind === 'r-group' || position.kind === 'attachment-point' ? [position.rGroupLabel] : []
  );
  return [...new Set(labels)];
}

/** Bounded Markush structures can be expanded into concrete structures (ADR 0013 §3, M4);
 *  unbounded ones need query-atom matching (M5). A `homology` R-group only makes the document
 *  unbounded when a position actually references it -- an unused definition expands to nothing. */
export function isBoundedMarkush(document: MarkushDrawingDocument): boolean {
  const referencedHomology = referencedRGroupLabels(document).some(
    (label) => document.rGroups[label]?.kind === 'homology'
  );
  const openRepeat = document.variablePositions.some(
    (position) => position.kind === 'repeat-unit' && position.countMax === undefined
  );
  return !referencedHomology && !openRepeat;
}

/** The next unused `R<n>` label, counting both defined and merely referenced labels. */
export function nextRGroupLabel(document: MarkushDrawingDocument): string {
  const used = [...Object.keys(document.rGroups), ...referencedRGroupLabels(document)];
  let max = 0;
  for (const label of used) {
    const match = /^R(\d+)$/.exec(label);
    if (match) max = Math.max(max, Number(match[1]));
  }
  return `R${max + 1}`;
}

/** True when `ringAtomIds` induces exactly one simple cycle in `graph`: at least three distinct
 *  nodes, each with exactly two neighbours inside the set, all connected. A fused/bridged
 *  selection (e.g. all ten naphthalene atoms, where the fusion atoms have three in-set
 *  neighbours) is rejected -- single-ring attachment points only in v1. */
export function isSingleRing(graph: MarkushGraph, ringAtomIds: readonly string[]): boolean {
  const ring = new Set(ringAtomIds);
  if (ring.size < 3 || ring.size !== ringAtomIds.length) return false;
  const nodeIds = new Set(graph.nodes.map((node) => node.id));
  if (ringAtomIds.some((id) => !nodeIds.has(id))) return false;
  const neighbours = new Map<string, string[]>(ringAtomIds.map((id) => [id, []]));
  for (const edge of graph.edges) {
    if (ring.has(edge.source) && ring.has(edge.target)) {
      neighbours.get(edge.source)!.push(edge.target);
      neighbours.get(edge.target)!.push(edge.source);
    }
  }
  if ([...neighbours.values()].some((list) => list.length !== 2)) return false;
  return connectedWithin(ringAtomIds, neighbours);
}

/** True when every id in `nodeIds` is reachable from the first through `adjacency`. */
export function connectedWithin(
  nodeIds: readonly string[],
  adjacency: ReadonlyMap<string, readonly string[]>
): boolean {
  if (nodeIds.length === 0) return true;
  const seen = new Set<string>([nodeIds[0]!]);
  const queue = [nodeIds[0]!];
  while (queue.length > 0) {
    for (const neighbour of adjacency.get(queue.shift()!) ?? []) {
      if (!seen.has(neighbour)) {
        seen.add(neighbour);
        queue.push(neighbour);
      }
    }
  }
  return seen.size === new Set(nodeIds).size;
}
